import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DollarSign, Loader2, Info } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { tr, uiLocale } from "@/lib/i18n";

const WINDOW_OPTIONS = [
  { key: "24h", get label() { return tr("Last 24 hours"); } },
  { key: "7d", get label() { return tr("Last 7 days"); } },
  { key: "30d", get label() { return tr("Last 30 days"); } },
  { key: "90d", get label() { return tr("Last 90 days"); } },
  { key: "all", get label() { return tr("All time"); } },
];

const PROVIDER_LABEL = {
  anthropic: "Claude",
  openai: "OpenAI",
  gemini: "Gemini",
};

const fmtUSD = (n) => {
  const v = Number(n || 0);
  if (v === 0) return "$0.00";
  if (v < 0.01) return `$${v.toFixed(4)}`;
  return `$${v.toFixed(2)}`;
};

const fmtInt = (n) =>
  Number(n || 0).toLocaleString(uiLocale(), { maximumFractionDigits: 0 });

export default function LlmCostPage() {
  const { user } = useAuth();
  const [window, setWindow] = useState("30d");
  const [propertyId, setPropertyId] = useState(""); // "" = all properties

  const { data: propsData } = useQuery({
    queryKey: ["admin-properties"],
    queryFn: () => api.adminListProperties(),
    enabled: user?.role === "admin",
  });
  // adminListProperties returns the array directly (not wrapped).
  const properties = Array.isArray(propsData) ? propsData : [];

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-llm-cost", window, propertyId],
    queryFn: () => api.adminGetLlmCost(window, propertyId || null),
    enabled: user?.role === "admin",
    refetchInterval: 30000, // background refresh every 30s
  });

  if (user?.role !== "admin") {
    return (
      <div className="p-10">
        <div className="font-serif-display text-2xl">{tr("Not authorized")}</div>
        <div className="text-sm text-[var(--ink-muted)] mt-2">{tr("Only admins can access this page.")}</div>
      </div>
    );
  }

  const totalCost = data?.total_cost ?? 0;
  const totalCalls = data?.total_calls ?? 0;
  const byModel = data?.by_model || [];
  const byTask = data?.by_task || [];
  const breakdown = data?.breakdown || [];

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-[var(--border)] bg-white sticky top-0 z-10">
        <div className="px-4 sm:px-10 py-5 sm:py-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="overline flex items-center gap-2">
              <DollarSign className="w-3 h-3" />{tr("Admin")}</div>
            <h1 className="font-serif-display text-3xl sm:text-4xl tracking-tight leading-tight mt-1">{tr("LLM Cost")}</h1>
            <div className="text-xs sm:text-sm text-[var(--ink-muted)] mt-1 max-w-2xl">{tr("Approximate spend by model, task, and property. Numbers are estimates from published provider list prices — actual Emergent Universal Key billing may differ. Tracking is forward-only from the day usage logging was enabled.")}</div>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="llm-cost-property"
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              className="px-3 py-2 text-sm border border-[var(--border)] bg-white font-mono-display"
              title={tr("Filter by property")}
            >
              <option value="">{tr("All properties")}</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <select
              data-testid="llm-cost-window"
              value={window}
              onChange={(e) => setWindow(e.target.value)}
              className="px-3 py-2 text-sm border border-[var(--border)] bg-white font-mono-display"
            >
              {WINDOW_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-10 py-6 sm:py-8 flex-1 overflow-auto">
        {isLoading && (
          <div className="flex items-center gap-2 text-[var(--ink-muted)]">
            <Loader2 className="w-4 h-4 animate-spin" />{tr("Loading…")}</div>
        )}
        {error && (
          <div className="text-red-600 text-sm">{tr("Failed to load: {message}", { message: error.message })}
          </div>
        )}
        {data && (
          <div className="max-w-6xl space-y-8">
            {/* Top-level totals */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard label={tr("Total cost")} value={fmtUSD(totalCost)} />
              <StatCard label={tr("Total calls")} value={fmtInt(totalCalls)} />
              <StatCard
                label={tr("Models used")}
                value={new Set(byModel.map((m) => `${m.provider}|${m.model}`)).size}
              />
              <StatCard
                label={tr("Tasks tracked")}
                value={new Set(byTask.map((t) => t.prompt_type)).size}
              />
            </div>

            {totalCalls === 0 && (
              <div className="p-6 border border-[var(--border)] bg-white flex items-start gap-3">
                <Info className="w-4 h-4 mt-0.5 text-[var(--ink-muted)] shrink-0" />
                <div className="text-sm text-[var(--ink-muted)]">{tr("No LLM calls recorded in this window yet. Cost tracking started when this feature shipped — historical calls aren't counted. Run a research, article, or image generation and the numbers will populate here within a few seconds.")}</div>
              </div>
            )}

            {/* Two side-by-side summaries */}
            <div className="grid lg:grid-cols-2 gap-6">
              <SummaryTable
                title={tr("By model")}
                rows={byModel}
                columns={[
                  { key: "model", label: tr("Model"),
                    render: (r) => (
                      <div>
                        <div className="font-serif-display leading-tight">{r.model}</div>
                        <div className="text-[10px] uppercase tracking-wider font-mono-display text-[var(--ink-muted)]">
                          {PROVIDER_LABEL[r.provider] || r.provider}
                        </div>
                      </div>
                    ),
                  },
                  { key: "calls", label: tr("Calls"), render: (r) => fmtInt(r.calls), align: "right" },
                  { key: "cost_usd", label: tr("Cost"), render: (r) => fmtUSD(r.cost_usd), align: "right", bold: true },
                ]}
              />
              <SummaryTable
                title={tr("By task")}
                rows={byTask}
                columns={[
                  { key: "prompt_type", label: tr("Task"),
                    render: (r) => (
                      <div className="font-serif-display leading-tight">{r.prompt_type}</div>
                    ),
                  },
                  { key: "calls", label: tr("Calls"), render: (r) => fmtInt(r.calls), align: "right" },
                  { key: "cost_usd", label: tr("Cost"), render: (r) => fmtUSD(r.cost_usd), align: "right", bold: true },
                ]}
              />
            </div>

            {/* Detailed breakdown */}
            <div>
              <div className="overline mb-2">{tr("Detailed breakdown — property × task × model")}</div>
              <div className="border border-[var(--border)] bg-white overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--border)] bg-[var(--secondary)]/40 text-[10px] uppercase tracking-wider font-mono-display text-[var(--ink-muted)]">
                      <th className="text-left px-3 py-2">{tr("Property")}</th>
                      <th className="text-left px-3 py-2">{tr("Task")}</th>
                      <th className="text-left px-3 py-2">{tr("Model")}</th>
                      <th className="text-right px-3 py-2">{tr("Calls")}</th>
                      <th className="text-right px-3 py-2">{tr("Input tok")}</th>
                      <th className="text-right px-3 py-2">{tr("Output tok")}</th>
                      <th className="text-right px-3 py-2">{tr("Images")}</th>
                      <th className="text-right px-3 py-2">{tr("Cost")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakdown.length === 0 && (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-3 py-6 text-center text-[var(--ink-muted)] text-xs"
                        >{tr("No entries yet.")}</td>
                      </tr>
                    )}
                    {breakdown.map((r, i) => (
                      <tr
                        key={`${r.property_id}-${r.prompt_type}-${r.provider}-${r.model}-${i}`}
                        className={
                          i !== breakdown.length - 1
                            ? "border-b border-[var(--border)]"
                            : ""
                        }
                      >
                        <td className="px-3 py-2">
                          {r.property_name || (
                            <span className="text-[var(--ink-muted)] italic">{tr("(none)")}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono-display text-[11px]">
                          {r.prompt_type}
                        </td>
                        <td className="px-3 py-2 font-mono-display text-[11px]">
                          {r.model}
                        </td>
                        <td className="px-3 py-2 text-right font-mono-display text-[11px]">
                          {fmtInt(r.calls)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono-display text-[11px]">
                          {fmtInt(r.input_tokens)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono-display text-[11px]">
                          {fmtInt(r.output_tokens)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono-display text-[11px]">
                          {r.images ? fmtInt(r.images) : "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-serif-display">
                          {fmtUSD(r.cost_usd)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-[10px] uppercase tracking-wider font-mono-display text-[var(--ink-muted)]">{tr("Refreshes every 30s · Prices are provider list rates as of Feb 2026 · OpenAI/Gemini token counts are estimated (chars ÷ 4) since the SDK does not expose exact usage")}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="border border-[var(--border)] bg-white px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider font-mono-display text-[var(--ink-muted)]">
        {label}
      </div>
      <div className="text-2xl font-serif-display leading-tight mt-1">
        {value}
      </div>
    </div>
  );
}

function SummaryTable({ title, rows, columns }) {
  return (
    <div>
      <div className="overline mb-2">{title}</div>
      <div className="border border-[var(--border)] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border)] bg-[var(--secondary)]/40 text-[10px] uppercase tracking-wider font-mono-display text-[var(--ink-muted)]">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`px-3 py-2 ${c.align === "right" ? "text-right" : "text-left"}`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-4 text-center text-[var(--ink-muted)] text-xs"
                >{tr("No entries yet.")}</td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr
                key={i}
                className={
                  i !== rows.length - 1
                    ? "border-b border-[var(--border)]"
                    : ""
                }
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`px-3 py-2 ${c.align === "right" ? "text-right" : "text-left"} ${
                      c.bold ? "font-serif-display" : ""
                    }`}
                  >
                    {c.render(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
